import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import type { PayrollRunFilters, RunStatusValue, RunTypeValue } from '@/features/payroll-runs/api'
import { PayrollRunsScreen } from '@/features/payroll-runs/payroll-runs-screen'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the runs list.
 *
 * `runs.$runId.tsx` is a CHILD of this route in flat file routing, so without
 * an Outlet the detail URL silently renders this list — the same shape
 * `accounting/invoices.tsx` documents.
 *
 * Filters live in URL search params so a filtered view is linkable and
 * survives a reload. The shell validates and owns the search shape; the
 * feature owns what it does with the values.
 *
 * The grant guard is presentation only; the API enforces `payroll_run.view`
 * on every run endpoint.
 */
const RUN_STATUSES: RunStatusValue[] = ['draft', 'confirmed', 'posted', 'paid', 'cancelled']
const RUN_TYPES: RunTypeValue[] = ['regular', 'off_cycle']

function isRunStatus(value: unknown): value is RunStatusValue {
  return typeof value === 'string' && (RUN_STATUSES as string[]).includes(value)
}

function isRunType(value: unknown): value is RunTypeValue {
  return typeof value === 'string' && (RUN_TYPES as string[]).includes(value)
}

export const Route = createFileRoute('/_authenticated/payroll/runs')({
  validateSearch: (search: Record<string, unknown>): PayrollRunFilters => ({
    search: typeof search.search === 'string' && search.search ? search.search : undefined,
    status: isRunStatus(search.status) ? search.status : undefined,
    run_type: isRunType(search.run_type) ? search.run_type : undefined,
    from: typeof search.from === 'string' && search.from ? search.from : undefined,
    to: typeof search.to === 'string' && search.to ? search.to : undefined,
    create: search.create === '1' ? '1' : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'payroll_run.view')) {
      throw redirect({ to: '/' })
    }
  },
  component: PayrollRunsRoute,
})

function PayrollRunsRoute() {
  const navigate = useNavigate()
  const children = useChildMatches()
  const params = Route.useSearch()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <PayrollRunsScreen
      params={params}
      onParamsChange={(next) => void navigate({ to: '/payroll/runs', search: next as never })}
    />
  )
}
